/**
 * Interview Service - Supabase Database Operations
 *
 * Handles all interview session CRUD operations including:
 * - Creating new interview sessions (campaigns) with secure link generation
 * - Fetching sessions by ID, room ID, or hash
 * - Updating session status and context
 * - Updating an existing campaign's configuration in place
 *   (`updateInterviewSessionConfig`), guarded by optimistic concurrency and
 *   throwing {@link CampaignConfigConflictError} as expected control flow on
 *   a stale write
 * - Managing interview responses (multi-response per campaign)
 * - Managing interview data (conversation, quotes)
 * - Completing responses and closing sessions
 *
 * @module services/interviewService
 */

import { getSupabase } from '../lib/supabase';
import { validateConversationSections } from '../lib/conversation-sections';
import { generateInterviewLink } from './linkService';
import { captureException } from '../lib/telemetry/posthog/frontend';
import { normalizeHttpUrl } from '../lib/security/safe-http-url';
import { deriveDetailsFromFormSchema, resolveDetails } from '../lib/form-schema-details';
import { DEFAULT_BOOKABLE_WINDOWS } from '../lib/integrations/calendar/default-windows';
import { normalizeConversationDefinition } from '../lib/conversation-definition';
import { getCallQuestionLimitIssue } from '../lib/call-question-limit';
import { validateFormFields } from '../lib/form-capabilities';
import type { ConversationExperienceConfig } from '../lib/multimodal-contract';
import type { AnalyticsResponse } from '../lib/campaign-analytics/normalize';
import type {
  InterviewSession,
  InterviewData,
  InterviewSessionStatus,
  ConversationTurn,
  ResearchEvent,
  VerifiedQuote,
  GeneratedLink,
  ProductResearch,
  FormField,
} from '../types';

/** One response as the campaign Overview loads it: answers, statuses and progress only. */
export type CampaignAnswerRow = AnalyticsResponse;

/**
 * Input for creating a new interview session.
 *
 * Phase 1 of the form-first redesign adds optional `form_schema`. When
 * provided, `createInterviewSession` derives a `details` blob from it (via
 * `deriveDetailsFromFormSchema`) so the unmodified `live-session` worker
 * keeps reading a useful prompt source. Callers may still pass `details`
 * explicitly; an explicit value always wins over the derivation.
 */
export interface CreateInterviewSessionInput {
  target_name: string;
  target_url?: string;
  goal: string;
  details?: string;
  product_research?: ProductResearch;
  output_language?: string;
  brand_id?: string;
  organization_id?: string;
  /**
   * Admin-designed form fields. Persisted to `interview_sessions.form_schema`
   * via the 13-arg `create_interview_session` RPC. Defaults to `[]` when
   * omitted, preserving backward compatibility with legacy callers.
   */
  form_schema?: FormField[];
  /** Versioned participation settings, snapshotted when a response starts. */
  conversation_config?: ConversationExperienceConfig | null;
}

/** Campaign settings update */
export interface SessionSettingsUpdate {
  max_responses?: number | null;
  allow_duplicate_emails?: boolean;
  /** Legacy storage flag. New conversations require names on their Name questions. */
  require_name?: boolean;
  /** Legacy storage flag. New conversations require emails on their Email questions. */
  require_email?: boolean;
  /** When true, camera is enabled (video + audio). When false, audio-only interviews. */
  video_enabled?: boolean;
  /** When true, camera is mandatory: no audio-only fallback. Only relevant when video_enabled is true. */
  require_video?: boolean;
  /** When true, hollie may request the respondent's screen and the respondent may volunteer it. Gated by the `screen_sharing` entitlement (Pro+); independent of `video_enabled`. */
  screen_share_enabled?: boolean;
  enforce_language?: string | null;
  /** Preferred language for generated content. */
  output_language?: string;
  /** Versioned participation settings, snapshotted when a response starts. */
  conversation_config?: ConversationExperienceConfig | null;
  /** Legacy storage mode. Name and Email questions now own identity collection. */
  identity_collection?: 'pre' | 'post' | 'never';
  /** Legacy storage depth. Each Explore question now owns its probing depth. */
  probing_depth?: number;
  opens_at?: string | null;
  closes_at?: string | null;
  /**
   * Optional per-campaign embed allowlist. NULL or empty array means
   * "embeddable anywhere". Each entry is either an exact origin
   * ("https://acme.com") or a single-label wildcard subdomain
   * ("https://*.acme.com"). Enforced by the create-response API and the
   * launcher/iframe shell at load time.
   */
  allowed_origins?: string[] | null;
  /**
   * On-submission behaviour. 'message' (default) renders the success
   * screen, optionally with a custom body. 'redirect' sends the
   * respondent to {@link completion_redirect_url} and is gated by the
   * completion_redirect entitlement (Business+); the DB trigger
   * rejects saves that violate the gate.
   */
  completion_action?: 'message' | 'redirect';
  /** Optional custom success-screen body. NULL falls back to default copy. */
  completion_message?: string | null;
  /** Destination URL when completion_action is 'redirect'. */
  completion_redirect_url?: string | null;
}

const supabase = getSupabase();

// ============================================================================
// FORM-SCHEMA DERIVATION (PHASE 1 → PHASE 2 BRIDGE)
// ============================================================================

// `deriveDetailsFromFormSchema` and `resolveDetails` moved to the worker-safe
// `lib/form-schema-details` module so the v1 API / MCP create path derives the
// `details` blob identically (an API-created campaign must drive hollie's
// questions the same way a wizard-created one does). Re-exported here so this
// module's public surface (and its tests) are unchanged.
export { deriveDetailsFromFormSchema };

/**
 * A publish-blocking issue on a single information block.
 *
 * - `missing_image`: the block has no uploaded image or selected GIF yet.
 * - `caption_pending`: the block has an image but no AI caption. A caption-less
 *   block is invisible to hollie at call time (the caption is her only surrogate
 *   for the image), so it is a silent coverage hole and blocks publish.
 */
export type InformationBlockIssue = {
  fieldId: string;
  reason: 'missing_image' | 'caption_pending';
};

/**
 * Validate the information blocks in a form schema for the publish gate.
 *
 * Returns one issue per information field that is missing its image, and one per
 * information field that has an image but no caption. Title and directive are
 * non-blocking: the caption pipeline backfills them and a respondent can
 * legitimately receive an untitled block. Non-information fields are ignored.
 *
 * @param fields The full form schema (mixed field types).
 * @returns Issues to surface as per-card markers; empty when every block is ok.
 */
export function validateInformationBlocks(fields: FormField[]): InformationBlockIssue[] {
  const issues: InformationBlockIssue[] = [];
  for (const field of fields) {
    if (field.type !== 'information') {
      continue;
    }
    if (field.contentVariant === 'text') continue;
    const hasUploadedImage =
      typeof field.imageStorageKey === 'string' && field.imageStorageKey.trim().length > 0;
    const hasKlipyImage = field.imageSource === 'klipy'
      && typeof field.imageUrl === 'string'
      && field.imageUrl.trim().length > 0;
    const hasImage = hasUploadedImage || hasKlipyImage;
    if (!hasImage) {
      issues.push({ fieldId: field.id, reason: 'missing_image' });
      continue;
    }
    const hasCaption = typeof field.caption === 'string' && field.caption.trim().length > 0;
    if (!hasCaption) {
      issues.push({ fieldId: field.id, reason: 'caption_pending' });
    }
  }
  return issues;
}

/**
 * A publish-blocking issue on a single calendar block.
 *
 * - `missing_connection`: the block has no resolved host (no `connectionId` or
 *   no `hostUserId`), so hollie has no calendar to book against. In teammate
 *   mode this also covers "no member selected yet".
 * - `empty_window`: the minimum notice plus the meeting length cannot fit
 *   inside the booking horizon (`minimumNoticeHours * 60 + durationMinutes >
 *   bookingHorizonDays * 24 * 60`), so the availability window is permanently
 *   empty and hollie would offer nothing. Both steppers are independent, so the
 *   invalid combination is authorable and must be caught at publish.
 * - `no_bookable_days`: the block's weekly availability has no bookable window,
 *   because every one is disabled, the list is explicitly empty, or every
 *   enabled window has `end <= start` (an inverted or midnight-crossing window,
 *   which produces zero availability at runtime). An absent list means the
 *   default Monday-Friday 09:00-17:00 windows, which are always bookable.
 */
export type CalendarBookingIssue = {
  fieldId: string;
  reason: 'missing_connection' | 'empty_window' | 'no_bookable_days';
};

/**
 * Validate the calendar blocks in a form schema for the publish gate.
 *
 * Returns one issue per calendar block with an unresolved host or an
 * unbookable custom-availability configuration. The directive never gates
 * publish: hollie's built-in behavior works without one. Non-calendar fields
 * are ignored.
 *
 * @param fields The full form schema (mixed field types).
 * @returns Issues to surface as per-card markers; empty when every block is ok.
 */
export function validateCalendarBookings(fields: FormField[]): CalendarBookingIssue[] {
  const issues: CalendarBookingIssue[] = [];
  for (const field of fields) {
    if (field.type !== 'calendar_booking') {
      continue;
    }
    const hasHost = typeof field.hostUserId === 'string' && field.hostUserId.trim().length > 0;
    const hasConnection =
      typeof field.connectionId === 'string' && field.connectionId.trim().length > 0;
    if (!hasHost || !hasConnection) {
      issues.push({ fieldId: field.id, reason: 'missing_connection' });
      continue;
    }
    // The notice window must leave room for at least one meeting inside the
    // horizon; when it does not, every computed slot is empty at runtime. This
    // is checked before custom windows because it makes any window unbookable.
    const noticeMinutes = field.minimumNoticeHours * 60;
    const horizonMinutes = field.bookingHorizonDays * 24 * 60;
    if (noticeMinutes + field.durationMinutes > horizonMinutes) {
      issues.push({ fieldId: field.id, reason: 'empty_window' });
      continue;
    }
    // bookableWindows is the single source of truth now that inherited
    // availability is gone. An absent set means the zero-config Mon-Fri
    // 09:00-17:00 default (always bookable); an explicit set is validated so a
    // block that offers no real window is caught at publish. A window only
    // contributes availability when it is enabled AND its end is strictly after
    // its start (string HH:MM compares lexicographically).
    const windows = field.bookableWindows ?? DEFAULT_BOOKABLE_WINDOWS;
    const anyBookable = windows.some((w) => w.enabled && w.end > w.start);
    if (!anyBookable) {
      issues.push({ fieldId: field.id, reason: 'no_bookable_days' });
    }
  }
  return issues;
}


// ============================================================================
// SESSION (CAMPAIGN) OPERATIONS
// ============================================================================

/**
 * Create a new interview session via the `create_interview_session` RPC.
 *
 * The RPC handles bcrypt hashing of the passcode server-side and resolves the
 * active organization context (entitlements are evaluated against
 * `p_organization_id`).
 *
 * **Phase 1 form-first behaviour:** when `input.form_schema` is non-empty
 * and `input.details` is absent or empty, `details` is auto-derived from the
 * schema via `deriveDetailsFromFormSchema`. An explicit `details` always
 * wins. The wizard rework (Wave 3) stops authoring `details` directly and
 * relies on this derivation; legacy call sites that still pass `details`
 * keep working unchanged.
 *
 * No auto-created `interview_data` row. The campaign starts with zero
 * responses; respondents create their own response row via
 * `create_interview_response`.
 *
 * @param input  Session inputs including the new `form_schema`.
 * @param userId Authenticated creator's UUID.
 * @param options.videoEnabled Whether the campaign requires video. Validated
 *                             server-side against the org's entitlements.
 * @returns The created session row + the generated room/passcode/link triple.
 */
export async function createInterviewSession(
  input: CreateInterviewSessionInput,
  userId: string,
  options?: { videoEnabled?: boolean }
): Promise<{ session: InterviewSession; link: GeneratedLink }> {
  // Generate room credentials (passcode will be hashed server-side)
  const link = generateInterviewLink();

  // Phase 1 bridge: when the wizard supplies a form_schema and no explicit
  // details, derive details from the schema so live-session keeps a useful
  // prompt source. Local computation only - never mutate the caller's input.
  const formSchema = input.form_schema ?? [];
  const sectionIssue = validateConversationSections(formSchema, input.conversation_config?.sections)[0];
  if (sectionIssue) throw new Error(sectionIssue.message);
  const hasVisualQuestionMedia = formSchema.some(field => field.type === 'image_choice' || field.type === 'image_click_map');
  const callIssue = getCallQuestionLimitIssue(formSchema.length ? formSchema : normalizeConversationDefinition({ details: input.details }).fields, input.conversation_config?.callsEnabled !== false);
  if (callIssue) throw new Error(callIssue.message);
  if (hasVisualQuestionMedia && !input.brand_id) throw new Error('Choose a workspace before adding visual question media.');
  if (input.conversation_config || hasVisualQuestionMedia) {
    const issue = validateFormFields(formSchema, { workspaceId: input.brand_id })[0];
    if (issue) throw new Error(issue.message);
  }
  const resolvedDetails = resolveDetails(input.details, formSchema);
  const atomicWorkspace = Boolean(input.brand_id && input.conversation_config);

  // Use RPC function which handles bcrypt hashing server-side
  const { data, error } = await supabase.rpc('create_interview_session', {
    p_user_id: userId,
    p_room_id: link.roomId,
    p_passcode: link.passcode,
    p_link_hash: link.hash,
    p_target_name: input.target_name,
    p_target_url: input.target_url ?? null,
    p_goal: input.goal,
    p_details: resolvedDetails,
    p_product_research: input.product_research ?? null,
    p_output_language: input.output_language ?? null,
    p_video_enabled: options?.videoEnabled ?? false,
    p_organization_id: input.organization_id ?? null,
    p_form_schema: formSchema,
    ...(input.conversation_config !== undefined ? { p_conversation_config: input.conversation_config } : {}),
    ...(atomicWorkspace ? { p_brand_id: input.brand_id } : {}),
  });

  if (error) {
    console.error('Error creating interview session via RPC:', error);
    const message = (error as { message?: string }).message ?? String(error);
    captureException(
      error instanceof Error ? error : new Error(message),
      'InterviewService.createInterviewSession',
      { userId, hasFormSchema: formSchema.length > 0, fieldCount: formSchema.length }
    );
    throw new Error(`Failed to create interview session: ${message}`);
  }

  // Legacy callers retain their original overload; versioned creation is atomic.
  if (input.brand_id && !atomicWorkspace) {
    const { error: brandError } = await supabase
      .from('interview_sessions')
      .update({ brand_id: input.brand_id })
      .eq('id', data);

    if (brandError) {
      console.error('Failed to set brand_id on session:', brandError);
    }
  }

  // Fetch the created session (no interview_data fetch - campaign starts with 0 responses)
  const { data: session, error: fetchError } = await supabase
    .from('interview_sessions')
    .select('*')
    .eq('id', data)
    .single();

  if (fetchError || !session) {
    throw new Error(`Failed to fetch created session: ${fetchError?.message}`);
  }

  return { session, link };
}

// Get interview session by ID
export async function getInterviewSession(sessionId: string): Promise<InterviewSession | null> {
  const { data, error } = await supabase
    .from('interview_sessions')
    .select('*')
    .eq('id', sessionId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw new Error(`Failed to fetch interview session: ${error.message}`);
  }

  return data;
}

// Get interview session by room_id
export async function getInterviewSessionByRoomId(roomId: string): Promise<InterviewSession | null> {
  const { data, error } = await supabase
    .from('interview_sessions')
    .select('*')
    .eq('room_id', roomId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw new Error(`Failed to fetch interview session: ${error.message}`);
  }

  return data;
}

// Get interview session by link hash
export async function getInterviewSessionByHash(hash: string): Promise<InterviewSession | null> {
  const { data, error } = await supabase
    .from('interview_sessions')
    .select('*')
    .eq('interview_link_hash', hash)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw new Error(`Failed to fetch interview session: ${error.message}`);
  }

  return data;
}

// Update interview session
export async function updateInterviewSession(
  sessionId: string,
  updates: Partial<Pick<InterviewSession, 'target_name' | 'target_url' | 'goal' | 'details' | 'product_research' | 'output_language' | 'expires_at'>>
): Promise<InterviewSession> {
  const { data, error } = await supabase
    .from('interview_sessions')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', sessionId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update interview session: ${error.message}`);
  }

  return data;
}

/**
 * The editable configuration of an existing campaign: everything the builder
 * canvas authors. `details` is deliberately absent; it is always re-derived
 * from `form_schema` on save so hollie's prompt source tracks the builder.
 */
export interface CampaignConfigUpdate {
  /** Omitted preserves the saved participation settings. */
  conversation_config?: ConversationExperienceConfig | null;
  /** Display title shown in the dashboard, sidebar, and breadcrumbs. */
  target_name: string;
  /** The conversation's goal, steering hollie's tone and follow-ups. */
  goal: string;
  /** Transcript / case-study language code. Omitted keys are left untouched. */
  output_language?: string;
  /** The full replacement form schema. Must be non-empty. */
  form_schema: FormField[];
}

/**
 * Thrown when an edit-campaign save finds no row matching both the session id
 * and the `updated_at` observed at load time: the campaign changed since it
 * was opened (or access was lost). Expected control flow for the optimistic
 * concurrency check; callers surface a reload affordance rather than an error
 * report.
 */
export class CampaignConfigConflictError extends Error {
  /**
   * @param sessionId The campaign whose save was rejected.
   */
  constructor(sessionId: string) {
    super(`Campaign ${sessionId} changed since it was loaded`);
    this.name = 'CampaignConfigConflictError';
  }
}

/**
 * Update an existing campaign's form configuration in place.
 *
 * Always re-derives `details` from the new `form_schema` (full re-derivation;
 * the builder is the source of truth after an edit), otherwise the live
 * session worker would keep prompting hollie with the old questions.
 *
 * Optimistic concurrency without a migration: the UPDATE filters on both the
 * session id and the `updated_at` the caller observed at load. When no row
 * matches, the row changed since load (or RLS access was lost) and a
 * {@link CampaignConfigConflictError} is thrown; that is expected control
 * flow, not a telemetry-worthy failure.
 *
 * @param sessionId         The campaign (interview_sessions row) to update.
 * @param update            The replacement configuration. `form_schema` must
 *                          be non-empty; the UI blocks this and the service
 *                          backstops it.
 * @param expectedUpdatedAt The `updated_at` value read when the editor loaded.
 * @returns The updated session row.
 * @throws {CampaignConfigConflictError} When the row changed since load.
 * @throws {Error} When `form_schema` is empty or the update fails.
 */
export async function updateInterviewSessionConfig(
  sessionId: string,
  update: CampaignConfigUpdate,
  expectedUpdatedAt: string,
  workspaceId?: string,
): Promise<InterviewSession> {
  if (update.form_schema.length === 0) {
    throw new Error('Cannot save a campaign without questions');
  }
  const sectionIssue = validateConversationSections(update.form_schema, update.conversation_config?.sections)[0];
  if (sectionIssue) throw new Error(sectionIssue.message);
  const callIssue = getCallQuestionLimitIssue(update.form_schema, update.conversation_config?.callsEnabled === true);
  if (callIssue) throw new Error(callIssue.message);
  const hasVisualQuestionMedia = update.form_schema.some(field => field.type === 'image_choice' || field.type === 'image_click_map');
  if (hasVisualQuestionMedia && !workspaceId) throw new Error('Choose a workspace before adding visual question media.');
  if (update.conversation_config || hasVisualQuestionMedia) {
    const issue = validateFormFields(update.form_schema, { workspaceId })[0];
    if (issue) throw new Error(issue.message);
  }

  const payload: Record<string, unknown> = {
    ...(update.conversation_config !== undefined ? { conversation_config: update.conversation_config } : {}),
    target_name: update.target_name,
    goal: update.goal,
    form_schema: update.form_schema,
    details: deriveDetailsFromFormSchema(update.form_schema),
    updated_at: new Date().toISOString(),
  };
  if (update.output_language !== undefined) {
    payload.output_language = update.output_language;
  }

  const { data, error } = await supabase
    .from('interview_sessions')
    .update(payload)
    .eq('id', sessionId)
    .eq('updated_at', expectedUpdatedAt)
    .select()
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to update campaign configuration: ${error.message}`);
  }
  if (!data) {
    throw new CampaignConfigConflictError(sessionId);
  }

  return data;
}

// Update campaign settings (max_responses, duplicate emails, etc.)
export async function updateSessionSettings(
  sessionId: string,
  settings: SessionSettingsUpdate
): Promise<InterviewSession> {
  const { data, error } = await supabase
    .from('interview_sessions')
    .update({
      ...settings,
      updated_at: new Date().toISOString(),
    })
    .eq('id', sessionId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update session settings: ${error.message}`);
  }

  return data;
}

// List all interview sessions for a user
export async function listInterviewSessions(userId: string): Promise<InterviewSession[]> {
  const { data, error } = await supabase
    .from('interview_sessions')
    .select('*')
    .eq('created_by', userId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to list interview sessions: ${error.message}`);
  }

  return data || [];
}

/**
 * List all interview sessions for an organization.
 * Uses organization_id column (set by RLS/trigger on insert) to scope sessions.
 *
 * @param orgId - Organization UUID to filter by
 * @returns Array of interview sessions belonging to the org
 */
export async function listInterviewSessionsByOrg(orgId: string): Promise<InterviewSession[]> {
  const { data, error } = await supabase
    .from('interview_sessions')
    .select('*')
    .eq('organization_id', orgId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to list org interview sessions: ${error.message}`);
  }

  return data || [];
}

/**
 * List all interview sessions in a workspace (Phase A: stored as `brand_id`).
 * Scopes by both `organization_id` (for RLS) and `brand_id` (workspace) so
 * the dashboard reflects the active workspace's campaigns only.
 *
 * @param orgId       Active organization UUID. Used as the RLS scope.
 * @param workspaceId Active workspace id (`brands.id`).
 * @returns           Array of interview sessions belonging to the workspace.
 */
export async function listInterviewSessionsByWorkspace(
  orgId: string,
  workspaceId: string,
): Promise<InterviewSession[]> {
  const { data, error } = await supabase
    .from('interview_sessions')
    .select('*')
    .eq('organization_id', orgId)
    .eq('brand_id', workspaceId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to list workspace interview sessions: ${error.message}`);
  }

  return data || [];
}

// Close a campaign session (admin action - prevents new responses)
export async function closeSession(sessionId: string): Promise<void> {
  const { error } = await supabase
    .from('interview_sessions')
    .update({
      status: 'completed' as InterviewSessionStatus,
      updated_at: new Date().toISOString(),
    })
    .eq('id', sessionId);

  if (error) {
    throw new Error(`Failed to close session: ${error.message}`);
  }
}

/** Re-open a completed campaign, setting its status back to active and clearing stale schedule dates. */
export async function reopenSession(sessionId: string): Promise<void> {
  // First fetch the session to check schedule dates
  const { data: session, error: fetchError } = await supabase
    .from('interview_sessions')
    .select('opens_at, closes_at')
    .eq('id', sessionId)
    .single();

  if (fetchError) {
    throw new Error(`Failed to fetch session for reopen: ${fetchError.message}`);
  }

  const now = new Date();
  const updates: Record<string, unknown> = {
    status: 'active' as InterviewSessionStatus,
    closes_at: null,
    updated_at: now.toISOString(),
  };

  // Clear opens_at if it's in the future (edge case safety)
  if (session?.opens_at && new Date(session.opens_at) > now) {
    updates.opens_at = null;
  }

  const { error } = await supabase
    .from('interview_sessions')
    .update(updates)
    .eq('id', sessionId);

  if (error) {
    throw new Error(`Failed to reopen session: ${error.message}`);
  }
}

// ============================================================================
// RESPONSE OPERATIONS (multi-response per campaign)
// ============================================================================

// Get first interview data for a session (backward compat - returns null for fresh sessions)
export async function getInterviewData(sessionId: string): Promise<InterviewData | null> {
  const { data, error } = await supabase
    .from('interview_data')
    .select('*')
    .eq('interview_session_id', sessionId)
    .order('response_number')
    .limit(1)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to fetch interview data: ${error.message}`);
  }

  return data;
}

// Get a specific response by its ID
export async function getInterviewResponse(responseId: string): Promise<InterviewData | null> {
  const { data, error } = await supabase
    .from('interview_data')
    .select('*')
    .eq('id', responseId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw new Error(`Failed to fetch interview response: ${error.message}`);
  }

  return data;
}

// Get all responses for a session, ordered by response_number
export async function getInterviewResponses(sessionId: string): Promise<InterviewData[]> {
  const { data, error } = await supabase
    .from('interview_data')
    .select('*')
    .eq('interview_session_id', sessionId)
    .order('response_number');

  if (error) {
    throw new Error(`Failed to fetch interview responses: ${error.message}`);
  }

  return data || [];
}

/** Answer columns the campaign Overview reads; transcripts are never selected. */
const ANSWER_ROW_COLUMNS = 'id, response_number, response_status, created_at, completed_at, form_answers, form_schema_snapshot, conversation_progress, calendar_bookings';
/** Rows per page; the loop continues until an empty page, whatever the server cap. */
const ANSWER_ROW_PAGE = 1000;

/**
 * Load every response of a campaign as answers only (no `conversation_turns`)
 * for the deterministic Overview. Uses the browser client under the existing
 * `interview_data_select` row policy and pages by id so large campaigns load
 * completely.
 */
export async function getCampaignAnswerRows(sessionId: string, signal?: AbortSignal): Promise<CampaignAnswerRow[]> {
  const rows: CampaignAnswerRow[] = [];
  let cursor: string | undefined;
  while (true) {
    let query = supabase.from('interview_data').select(ANSWER_ROW_COLUMNS)
      .eq('interview_session_id', sessionId)
      .order('id', { ascending: true })
      .limit(ANSWER_ROW_PAGE);
    if (cursor) query = query.gt('id', cursor);
    if (signal) query = query.abortSignal(signal);
    const { data, error } = await query;
    if (error) throw new Error(`Failed to load campaign answers: ${error.message}`);
    if (!data?.length) break;
    rows.push(...(data as unknown as CampaignAnswerRow[]));
    cursor = data[data.length - 1].id;
  }
  return rows;
}

/**
 * Per-session response summary: how many responses a campaign has and the
 * non-empty respondent names. Returned keyed by session id so the dashboard
 * can render counts and search without a row-per-session round trip.
 */
export interface ResponseSummaries {
  /** Response count per session id. Every requested id is present (0 if none). */
  counts: Record<string, number>;
  /** Non-empty interviewee names per session id. */
  names: Record<string, string[]>;
}

/**
 * Fetch response counts and respondent names for many sessions in ONE query.
 *
 * Replaces the AdminDashboard N+1 (one getInterviewResponses call per
 * campaign, each pulling every column of every response). This selects only
 * `interview_session_id` + `interviewee_name` for all sessions via a single
 * `.in()` filter and buckets the rows client-side. Every requested session id
 * appears in both maps (count 0, empty names) so callers can index safely.
 *
 * @param sessionIds - The interview_session ids to summarise.
 * @returns Counts and names keyed by session id.
 */
export async function getResponseSummariesForSessions(
  sessionIds: string[],
): Promise<ResponseSummaries> {
  const counts: Record<string, number> = {};
  const names: Record<string, string[]> = {};
  // Seed every requested id so the dashboard can index without `?? 0` guards
  // diverging from the data layer.
  for (const id of sessionIds) {
    counts[id] = 0;
    names[id] = [];
  }

  if (sessionIds.length === 0) {
    return { counts, names };
  }

  const { data, error } = await supabase
    .from('interview_data')
    .select('interview_session_id, interviewee_name')
    .in('interview_session_id', sessionIds);

  if (error) {
    throw new Error(`Failed to fetch response summaries: ${error.message}`);
  }

  for (const row of (data ?? []) as Array<{
    interview_session_id: string;
    interviewee_name?: string | null;
  }>) {
    const sid = row.interview_session_id;
    if (!(sid in counts)) {
      counts[sid] = 0;
      names[sid] = [];
    }
    counts[sid] += 1;
    if (row.interviewee_name) {
      names[sid].push(row.interviewee_name);
    }
  }

  return { counts, names };
}

// Save/update response data by response ID
export async function saveResponseData(
  responseId: string,
  data: {
    conversation_turns?: ConversationTurn[];
    research_events?: ResearchEvent[];
    verified_quotes?: VerifiedQuote[];
  }
): Promise<InterviewData> {
  const { data: result, error } = await supabase
    .from('interview_data')
    .update({
      ...data,
      updated_at: new Date().toISOString(),
    })
    .eq('id', responseId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to save response data: ${error.message}`);
  }

  return result;
}

/**
 * Enforces http(s) schemes on interviewee link fields before storage (S7).
 *
 * These values are rendered as anchor hrefs in the admin panel, so a stored
 * javascript:/data: URL would execute in the admin session. Scheme-less
 * values are normalized to https://; empty strings pass through so fields
 * can be cleared.
 *
 * @param data - Interviewee update payload (not mutated).
 * @returns A sanitized copy with URL fields normalized.
 * @throws Error when a URL field carries a non-http(s) scheme.
 */
function sanitizeIntervieweeUrls<
  T extends { interviewee_linkedin?: string; interviewee_company_url?: string }
>(data: T): T {
  const sanitized = { ...data };
  for (const field of ['interviewee_linkedin', 'interviewee_company_url'] as const) {
    const value = sanitized[field];
    if (typeof value === 'string' && value.trim()) {
      const normalized = normalizeHttpUrl(value);
      if (!normalized) {
        throw new Error(`${field} must be a valid http(s) URL`);
      }
      sanitized[field] = normalized;
    }
  }
  return sanitized;
}

// Update interviewee information by response ID
export async function updateResponseIntervieweeData(
  responseId: string,
  data: {
    interviewee_name?: string;
    interviewee_email?: string;
    interviewee_job?: string;
    interviewee_company?: string;
    interviewee_linkedin?: string;
    interviewee_company_url?: string;
  }
): Promise<InterviewData> {
  const { data: result, error } = await supabase
    .from('interview_data')
    .update({
      ...sanitizeIntervieweeUrls(data),
      updated_at: new Date().toISOString(),
    })
    .eq('id', responseId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update response interviewee data: ${error.message}`);
  }

  return result;
}

// Mark a response as completed (does NOT change session status)
export async function completeResponse(responseId: string): Promise<void> {
  const { error } = await supabase
    .from('interview_data')
    .update({
      response_status: 'completed',
      completed_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', responseId);

  if (error) {
    throw new Error(`Failed to complete response: ${error.message}`);
  }
}

/**
 * Resolves the caller's Supabase access token for an authenticated delete.
 *
 * Throws if no token is available, since the server-side delete (which owns
 * both R2 cleanup and the DB delete) cannot run without it. Failing here keeps
 * us from silently leaving respondent video orphaned in R2 (audit A15).
 *
 * @returns The Supabase JWT access token
 */
async function requireAccessToken(): Promise<string> {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) {
    throw new Error(`Failed to get auth session: ${error.message}`);
  }
  if (!session?.access_token) {
    throw new Error('Authentication required to delete: no access token available');
  }
  return session.access_token;
}

/**
 * Parses the error message from a failed delete-endpoint response.
 *
 * @param response - The fetch Response
 * @returns A human-readable error string
 */
async function deleteErrorMessage(response: Response): Promise<string> {
  const data = (await response.json().catch(() => ({}))) as { error?: string };
  return data.error || response.statusText || `status ${response.status}`;
}

/**
 * Deletes an interview session, its responses (FK cascade), and all associated
 * video files in R2.
 *
 * The entire operation runs server-side via the delete endpoint, which removes
 * R2 objects FIRST and only deletes the DB rows once cleanup fully succeeds. If
 * the server refuses (any R2 object failed to delete), this throws and the rows
 * are preserved, so respondent video (PII) is never orphaned in R2 (audit A15).
 *
 * @param sessionId - The interview session id to delete
 * @throws If authentication is missing or the server delete fails
 */
export async function deleteInterviewSession(sessionId: string): Promise<void> {
  const token = await requireAccessToken();

  const response = await fetch(`/api/videos/${sessionId}/delete`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const message = await deleteErrorMessage(response);
    const error = new Error(`Failed to delete interview session: ${message}`);
    captureException(error, 'InterviewService.deleteInterviewSession', { interviewId: sessionId });
    throw error;
  }
}

/**
 * Deletes a single interview response (one interview_data row) and its video
 * files in R2.
 *
 * Like deleteInterviewSession, the server deletes R2 objects first and removes
 * the response row only on full cleanup success; a refusal throws here so the
 * row keeps pointing at any surviving respondent video instead of orphaning it
 * (audit A15).
 *
 * @param responseId - The interview_data id (response) to delete
 * @param sessionId - The parent session id (for the endpoint path)
 * @throws If authentication is missing or the server delete fails
 */
export async function deleteInterviewResponse(responseId: string, sessionId: string): Promise<void> {
  const token = await requireAccessToken();

  const response = await fetch(`/api/videos/${sessionId}/responses/${responseId}/delete`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const message = await deleteErrorMessage(response);
    const error = new Error(`Failed to delete interview response: ${message}`);
    captureException(error, 'InterviewService.deleteInterviewResponse', { interviewId: sessionId, responseId });
    throw error;
  }
}

/**
 * Get the total count of interview sessions for the current organization.
 * Used for onboarding telemetry to determine if a user is creating their first campaign.
 */
export async function getInterviewSessionCount(orgId: string): Promise<number> {
  const { count, error } = await supabase
    .from('interview_sessions')
    .select('*', { count: 'exact', head: true })
    .eq('organization_id', orgId);

  if (error) {
    throw new Error(`Failed to count interview sessions: ${error.message}`);
  }

  return count || 0;
}

export default {
  createInterviewSession,
  deriveDetailsFromFormSchema,
  validateInformationBlocks,
  getInterviewSession,
  getInterviewSessionByRoomId,
  getInterviewSessionByHash,
  updateInterviewSession,
  updateInterviewSessionConfig,
  updateSessionSettings,
  listInterviewSessions,
  listInterviewSessionsByOrg,
  listInterviewSessionsByWorkspace,
  closeSession,
  reopenSession,
  getInterviewSessionCount,
  getInterviewData,
  getInterviewResponse,
  getInterviewResponses,
  saveResponseData,
  updateResponseIntervieweeData,
  completeResponse,
  deleteInterviewSession,
  deleteInterviewResponse,
};
